import { Either, Fail, Success } from 'monet';

/**
 * @description Запрос на редактирование сделки.
 * @param {string}  channel
 * @param {number}  dealId
 * @param {string}  doctor
 * @param {string}  funnel
 * @param {string}  service
 * @param {string}  source
 * @param {string}  stage
 * @param {boolean} stageIsChanged
 * @param {boolean} funnelIsChanged
 * @param {string}  rejectionReason
 * @param {boolean} needsCreatingRejectionReason
 * @constructor
 */
export function EditDealRequest(
  channel,
  dealId,
  doctor,
  funnel,
  service,
  source,
  stage,
  stageIsChanged,
  funnelIsChanged,
  rejectionReason,
  needsCreatingRejectionReason,
) {
  this.getDealId = () => (dealId);
  this.toQuery = () => ({
    channel,
    doctor,
    funnel,
    service,
    source,
    stage,
    stage_is_changed: stageIsChanged,
    funnel_is_changed: funnelIsChanged,
    rejection_reason: rejectionReason,
    needs_creating_rejection_reason: needsCreatingRejectionReason,
  });
}

/**
 * @description Причина отказа.
 * @param {number} id   Идентификатор.
 * @param {string} name Название.
 * @constructor
 */
export function RejectionReason(id, name) {
  this.id = id;
  this.name = name;
  this.errors = [];
}

/**
 * @description Запрос на создание этапа.
 * @param {string}      name
 * @param {string}      color
 * @param {string}      funnelId
 * @param {string|null} stageId
 * @constructor
 */
export function CreationStageRequest(
  name,
  color,
  funnelId,
  stageId = null,
) {
  this.getFunnelId = () => (funnelId);
  this.getStageId = () => (stageId);
  this.toQuery = () => ({
    name,
    color,
  });
}

/**
 * @description Шлюз данных CRM.
 * @param {Axios} axios HTTP-клиент.
 * @constructor
 */
export default function CrmGateway(axios) {
  /**
   * @description Добавить примечание к сделке.
   * @param {number} dealId
   * @param {string} note
   * @returns {Promise<Either>}
   */
  this.addDealNote = (dealId, note) => axios
    .post(`/api/crm-deals/${dealId}/note`, { note })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Создать сделку.
   * @param {object} request Данные запроса.
   * @returns {Promise<Either>}
   */
  this.createDeal = (request) => axios
    .post(
      '/api/crm-deals',
      {
        channel: request.channel,
        doctor: request.doctor,
        funnel: request.funnel,
        note: request.note,
        patient: request.patient,
        service: request.service,
        source: request.source,
        stage: request.stage,
        title: request.title,
        rejection_reason: request.rejectionReason,
        needs_creating_rejection_reason: request.needsCreatingRejectionReason,
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создать сделку с новым пациентом.
   * @param {object} request Данные запроса.
   * @returns {Promise<Either>}
   */
  this.createNewPatientDeal = (request) => axios
    .post(
      '/api/crm-new-patient-deals',
      {
        channel: request.channel,
        doctor: request.doctor,
        funnel: request.funnel,
        note: request.note,
        patient_birth_at: request.patientBirthAt,
        patient_name: request.patientName,
        patient_patronymic: request.patientPatronymic,
        patient_phone: request.patientPhone,
        patient_phone_type: request.patientPhoneType,
        patient_surname: request.patientSurname,
        service: request.service,
        source: request.source,
        stage: request.stage,
        title: request.title,
        rejection_reason: request.rejectionReason,
        needs_creating_rejection_reason: request.needsCreatingRejectionReason,
        agree_mailing: request.agreeMailing,
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Изменить этап заявки.
   * @param {number} dealId Идентификатор заявки.
   * @param {number} stage  Этап.
   * @returns {Promise<Either>}
   */
  this.changeDealStage = (dealId, stage) => axios
    .put(
      `/api/crm-deals/${dealId}/stage`,
      {
        stage,
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить причину отказа.
   * @param {number} rejectionId
   * @returns {Promise}
   */
  this.deleteRejectionReason = (rejectionId) => axios
    .delete(`/api/crm-rejection-reasons/${rejectionId}`)
    .then(
      (response) => Either.of(response.data.title),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Редактирование сделки.
   * @param {EditDealRequest} request
   * @returns {Promise}
   */
  this.editDeal = (request) => axios
    .put(`/api/crm-deals/${request.getDealId()}`, request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить причины отказа.
   * @returns {Promise<Either>}
   */
  this.viewRejectionReasonList = () => {
    const makeRejectionReason = (item) => new RejectionReason(item.id, item.name);
    return axios
      .get('/api/crm-rejection-reasons')
      .then(
        async (response) => {
          const list = (response.data.data || []).map(makeRejectionReason);
          return Success({ actions: response.data.actions, list });
        },
        async (error) => {
          if (error.isAxiosError) {
            if (error.response && error.response.status === 403) {
              return Fail(error.response.data);
            }
            if (!error.response) {
              throw new Error('Network error: сервер по адресу /api/crm-rejection-reasons не ответил на запрос');
            }
          }
          throw error;
        },
      );
  };

  /**
   * @description Получить опции заявки.
   * @returns {Promise<Either>}
   */
  this.viewDealOptions = () => axios
    .get('/api/crm-deal-options')
    .then(
      (response) => Either.of(response.data).map(({
        allowed_actions: allowedActions,
        channels,
        doctors,
        funnels,
        note_max_length: noteMaxLength,
        patients,
        services,
        sources,
        stages,
        title,
        title_max_length: titleMaxLength,
        rejection_reasons: rejectionReasons,
        period_types: periodTypes,
        is_required: isRequired,
      }) => ({
        channels,
        doctors,
        funnels,
        isAllowedNewPatient: allowedActions.includes('create-patients'),
        isAllowedViewPhone: allowedActions.includes('view-patient-phone'),
        isAllowedInitiateCall: allowedActions.includes('initiate-calls'),
        noteMaxLength,
        patients,
        services,
        sources,
        stages,
        title,
        titleMaxLength,
        rejectionReasons,
        isAdvertisingChannelRequired: isRequired,
        periodTypes: periodTypes.map(({ from_to: [from, to], title: periodTitle, value }) => ({
          periodDates: [new Date(from), new Date(to)],
          title: periodTitle,
          value,
        })),
      })),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить историю по заявке.
   * @param {number} dealId
   * @returns {Promise<Either>}
   */
  this.viewDealHistory = (dealId) => axios
    .get(`/api/crm-deals/${dealId}/history`)
    .then(
      (response) => Either.of(response.data).map(({
        history_list: historyList,
      }) => ({
        historyList,
      })),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
          || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удаление примечания CRM-сделки по действию.
   * @param {string} actionId
   * @returns {Promise<Either>}
   */
  this.deleteCrmDealNoteByAction = (actionId) => axios
    .delete(`/api/crm-deals-history/${actionId}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить заявку.
   * @param {number} dealId
   * @returns {Promise<Either>}
   */
  this.viewDeal = (dealId) => axios
    .get(`/api/crm-deals/${dealId}`)
    .then(
      (response) => Either.of(response.data).map(({
        title,
        patient_full_name: patientName,
        patient_age: patientAge,
        patient_labels: patientLabels,
        patient_phone: patientPhone,
        patient_id: patientId,
        created_at: createdAt,
        patient_phone_title: phoneTitle,
        patient_is_archive: patientIsArchive,
        source,
        source_name: sourceName,
        stage,
        funnel,
        service,
        doctor,
        channel,
        rejection_reason: rejectionReason,
      }) => ({
        channel,
        doctor,
        funnel,
        patientName,
        patientAge,
        patientLabels,
        patientPhone,
        phoneTitle,
        patientId,
        patientIsArchive,
        service,
        source,
        sourceName,
        stage,
        title,
        createdAt,
        rejectionReason,
      })),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Изменить цвет стадии воронки.
   * @param {string} funnel Воронка.
   * @param {string} id     Идентификатор стадии.
   * @param {string} color  Цвет.
   * @returns {Promise<Either>}
   */
  this.changeFunnelStageColor = (funnel, id, color) => axios
    .put(`/api/crm-funnels/${funnel}/stage/${id}/color`, { color })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создать этап воронки.
   * @param {string} funnelId
   * @param {Object} params
   * @returns {Promise}
   */
  this.createStage = (funnelId, params) => axios
    .post(`/api/crm-funnels/${funnelId}/stages`, params)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if ((error.isAxiosError && error.response.status === 403)
          || error.response.status === 400
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить этап воронки.
   * @param {string} funnelId
   * @param {string} stageId
   * @returns {Promise}
   */
  this.deleteStage = (funnelId, stageId) => axios
    .delete(`/api/crm-funnels/${funnelId}/stage/${stageId}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if ((error.isAxiosError && error.response.status === 403)
          || error.response.status === 400
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Редактировать этап воронки.
   * @param {string} funnelId
   * @param {string} stageId
   * @param {Object} params
   * @returns {Promise}
   */
  this.editStage = (funnelId, stageId, params) => axios
    .put(`/api/crm-funnels/${funnelId}/stage/${stageId}`, params)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if ((error.isAxiosError && error.response.status === 403)
          || error.response.status === 400
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать воронку.
   * @param {string} funnel                  Воронка.
   * @param {number} page                    Страница.
   * @param {string} dateFrom                Дата от.
   * @param {string} dateTo                  Дата до.
   * @param {string} selectedService         Выбранная услуга.
   * @param {string} selectedDoctor          Выбранный доктор.
   * @param {string} selectedStatus          Выбранный статус.
   * @param {string} selectedChannel         Выбранный канал.
   * @param {string} selectedSource          Выбранный источник.
   * @param {string} selectedRejectionReason Выбранная причина отказа.
   * @param {array} selectedPatients         Выбранный пациенты.
   * @returns {Promise<Either>}
   */
  this.viewFunnel = (
    funnel,
    page = 1,
    dateFrom = '',
    dateTo = '',
    selectedService = '',
    selectedDoctor = '',
    selectedStatus = '',
    selectedChannel = '',
    selectedSource = '',
    selectedRejectionReason = '',
    selectedPatients = [],
  ) => axios
    .get(
      `/api/crm-funnels/${funnel}`,
      {
        params: {
          page,
          date_from: dateFrom,
          date_to: dateTo,
          service: selectedService,
          doctor: selectedDoctor,
          status: selectedStatus,
          channel: selectedChannel,
          source: selectedSource,
          rejection_reason: selectedRejectionReason,
          patients: selectedPatients,
        },
      },
    )
    .then(
      (response) => Either.of(response.data).map(({
        deals_per_stages: dealsPerStages,
        stages,
      }) => ({
        dealsPerStages: dealsPerStages.map((deals) => deals.map(({
          patient,
          phone_number: phoneNumber,
          is_initial: isInitial,
          id: dealId,
          count_new_tasks: countNewTasks,
          is_archive: isArchive,
        }) => ({
          patient,
          phoneNumber,
          isInitial,
          dealId,
          countNewTasks,
          isArchive,
        }))),
        stages: stages.map(({
          name,
          color,
          id: stageId,
          count_deals: countDeals,
          is_default: isDefault,
        }) => ({
          name,
          color,
          stageId,
          countDeals,
          isDefault,
        })),
      })),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать выбор воронки.
   * @returns {Promise<Either>}
   */
  this.viewFunnelSelection = () => axios
    .get('/api/crm-funnels')
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Обновить этап заявки.
   * @param dealId
   * @param stageId
   * @returns {Promise<Either<unknown, any>>}
   */
  this.updateDealStage = (dealId, stageId) => axios
    .put(`/api/crm-deals/${dealId}/stage/${stageId}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
}
